import { Stack, Typography } from '@mui/material';

import { AlertTriangleFilledIcon } from '@/components/icons/AlertTriangleFilledIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { COLORS } from '@/styles/tokens/colors';

export interface AISuggestionReviewCellProps {
  label: string;
  values: AISuggestionReviewCellDisplayItemProps[];
}

/**
 * The shape row-variant authors use to build `columnsData`. Mirrors the
 * cell's public props so trailing cells render `label` + `values`. The
 * leading cell uses `AISuggestionReviewLeadingCell`, which doesn't take a
 * label, so consumers can't pass label-only fields meant for that cell here.
 */
export type AISuggestionReviewColumnData = Pick<
  AISuggestionReviewCellProps,
  'label' | 'values'
>;

/**
 * A trailing cell containing an eyebrow label and value(s) of an AI
 * suggestion row. Use `AISuggestionReviewLeadingCell` for the first cell.
 */
export function AISuggestionReviewCell({
  label,
  values,
}: AISuggestionReviewCellProps) {
  return (
    <Stack direction="column" gap={0.25} justifyContent="center" height="100%">
      <Typography variant="eyebrow-xs">{label}</Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {values.map((value, index) => (
          <AISuggestionReviewCellDisplayItem
            // Index-keyed: displayText is not unique (e.g. multiple "Unknown"
            // principals), and duplicate keys corrupt reconciliation when the
            // values transition (loading stubs -> resolved names).
            key={index}
            displayText={value.displayText}
            showWarning={value.showWarning}
            warningTooltip={value.warningTooltip}
            isLast={index === values.length - 1}
          />
        ))}
      </Stack>
    </Stack>
  );
}

export interface AISuggestionReviewCellDisplayItemProps {
  displayText: string;
  showWarning: boolean;
  warningTooltip?: string;
  notes?: string | null;
  isLast?: boolean;
}

function AISuggestionReviewCellDisplayItem({
  displayText,
  showWarning,
  warningTooltip,
  isLast = false,
}: AISuggestionReviewCellDisplayItemProps) {
  return showWarning ? (
    <Stack direction="row" data-testid="ai-suggestion-cell-warning">
      <Tooltip title={warningTooltip}>
        <Stack direction="row" gap={0.5} alignItems="center">
          <AlertTriangleFilledIcon
            fill={COLORS.FUNCTIONAL.WARNING['500']}
            color={COLORS.FUNCTIONAL.WARNING['500']}
            size={16}
          />
          <Typography
            variant="body-xs"
            color={COLORS.FUNCTIONAL.WARNING['500']}
          >
            {displayText}
            {isLast ? '' : ','}
          </Typography>
        </Stack>
      </Tooltip>
    </Stack>
  ) : (
    <Typography variant="body-xs" data-testid="ai-suggestion-cell-normal">
      {displayText}
      {isLast ? '' : ','}
    </Typography>
  );
}
